/**
 * Coda di upload (spec §9): un file per volta, progress per file, retry singolo.
 *
 * Seriale e non parallelo: il backend fa da proxy verso Graph con un limite di
 * concorrenza, e per un cliente che carica 30 fatture da una ADSL saturare la
 * banda in salita peggiora tutto.
 *
 * ## La destinazione appartiene al file, non al momento in cui parte
 *
 * Ogni item si porta dietro la cartella scelta **quando è stato messo in coda**.
 * Prenderla dalla cartella corrente al momento dell'invio sembrava equivalente,
 * e non lo era: la coda e' seriale e il pannello resta a schermo mentre si
 * naviga, quindi fra il gesto e la partenza l'utente puo' essere altrove.
 *
 * Due modi in cui si rompeva, visti in produzione (upload negato con percorso
 * vuoto, cioe' la radice, dove nessun cliente puo' scrivere):
 *
 *  - **«Riprova»** su un file fallito rimandava il file nella cartella aperta in
 *    quel momento, non in quella dove era stato lasciato cadere;
 *  - un file aggiunto mentre la coda **stava gia' girando** veniva raccolto dal
 *    ciclo in corso, che aveva catturato la cartella di prima: `drain` esce
 *    subito se `running`, e il ciclo vecchio continua a svuotare `pending`.
 *
 * Con la destinazione dentro l'item spariscono entrambi, e `uploadOne` non
 * dipende piu' dalla cartella corrente: niente closure da tenere fresca.
 */
import { useCallback, useRef, useState } from 'react';
import { ApiError, SMALL_UPLOAD_LIMIT, api, uploadSmall } from '../lib/api.js';

export type UploadStatus = 'in-coda' | 'in-corso' | 'completato' | 'errore' | 'annullato';

export type UploadItem = {
  id: string;
  file: File;
  /** Cartella scelta al momento dell'accodamento. `undefined` = radice dell'area. */
  dirRef: string | undefined;
  /** Nome della cartella, per dirlo nel pannello: il ref non si legge. */
  dirName: string;
  status: UploadStatus;
  /** 0..1 */
  progress: number;
  error?: string;
  /** Nome finale: SharePoint rinomina in caso di conflitto. */
  finalName?: string;
  renamed?: boolean;
};

let counter = 0;
const nextId = () => `up-${Date.now()}-${(counter += 1)}`;

export function useUploadQueue(
  dirRef: string | undefined,
  dirName: string,
  onFileDone: () => void,
) {
  const [items, setItems] = useState<UploadItem[]>([]);
  const running = useRef(false);
  const controllers = useRef(new Map<string, AbortController>());
  // La coda vive in un ref: il worker non deve dipendere dai re-render.
  const pending = useRef<UploadItem[]>([]);

  const patch = useCallback((id: string, changes: Partial<UploadItem>) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...changes } : it)));
  }, []);

  const uploadOne = useCallback(
    async (item: UploadItem) => {
      const controller = new AbortController();
      controllers.current.set(item.id, controller);
      patch(item.id, { status: 'in-corso', progress: 0, error: undefined });

      try {
        if (item.file.size <= SMALL_UPLOAD_LIMIT) {
          const res = await uploadSmall(
            item.dirRef,
            item.file,
            (fraction) => patch(item.id, { progress: fraction }),
            controller.signal,
          );
          patch(item.id, {
            status: 'completato',
            progress: 1,
            finalName: res.name,
            renamed: res.renamed,
          });
        } else {
          const session = await api.openUploadSession(item.dirRef, item.file.name, item.file.size);
          const total = item.file.size;
          let start = 0;

          while (start < total) {
            const end = Math.min(start + session.chunkSize, total);
            const slice = new Uint8Array(await item.file.slice(start, end).arrayBuffer());
            const res = await api.putChunk(
              session.uploadId,
              slice,
              start,
              end - 1,
              total,
              controller.signal,
            );
            if (res.done) {
              patch(item.id, {
                status: 'completato',
                progress: 1,
                finalName: res.name ?? item.file.name,
                renamed: (res.name ?? item.file.name) !== item.file.name,
              });
              break;
            }
            // Il backend dice da dove ripartire: è lui l'autorità sull'offset.
            start = res.uploadedBytes ?? end;
            patch(item.id, { progress: start / total });
          }
        }
        onFileDone();
      } catch (err) {
        if ((err as { name?: string }).name === 'AbortError') {
          patch(item.id, { status: 'annullato' });
          return;
        }
        patch(item.id, {
          status: 'errore',
          error: err instanceof ApiError ? err.message : 'Caricamento non riuscito.',
        });
      } finally {
        controllers.current.delete(item.id);
      }
    },
    // Niente `dirRef`: la destinazione viaggia con l'item.
    [onFileDone, patch],
  );

  const drain = useCallback(async () => {
    if (running.current) return;
    running.current = true;
    try {
      while (pending.current.length > 0) {
        const next = pending.current.shift()!;
        await uploadOne(next);
      }
    } finally {
      running.current = false;
    }
  }, [uploadOne]);

  const enqueue = useCallback(
    (files: File[]) => {
      const added = files.map<UploadItem>((file) => ({
        id: nextId(),
        file,
        dirRef,
        dirName,
        status: 'in-coda',
        progress: 0,
      }));
      setItems((prev) => [...prev, ...added]);
      pending.current.push(...added);
      void drain();
    },
    [dirRef, dirName, drain],
  );

  const retry = useCallback(
    (id: string) => {
      const item = items.find((it) => it.id === id);
      if (!item) return;
      patch(id, { status: 'in-coda', progress: 0, error: undefined });
      pending.current.push({ ...item, status: 'in-coda', progress: 0 });
      void drain();
    },
    [items, patch, drain],
  );

  const cancel = useCallback((id: string) => {
    controllers.current.get(id)?.abort();
    pending.current = pending.current.filter((it) => it.id !== id);
    setItems((prev) =>
      prev.map((it) =>
        it.id === id && (it.status === 'in-coda' || it.status === 'in-corso')
          ? { ...it, status: 'annullato' }
          : it,
      ),
    );
  }, []);

  /** Toglie dalla lista solo ciò che non è più in movimento. */
  const clearFinished = useCallback(() => {
    setItems((prev) => prev.filter((it) => it.status === 'in-corso' || it.status === 'in-coda'));
  }, []);

  const active = items.filter((it) => it.status === 'in-corso' || it.status === 'in-coda').length;

  return { items, enqueue, retry, cancel, clearFinished, active };
}
